import React, { useCallback, useContext, useEffect, useState } from 'react';
import { MoveVariationRow } from '../DraggableVariation/DraggableVariation.view';
import { DraggableVariation } from '../DraggableVariation';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import updateImmutable from 'immutability-helper';
import { SlateWrapperViewProps } from '../SlateWrapper/SlateWrapper.view';
import { initialParagraph } from '../../states/initial';
import { getSelectedProfilesWithBoards } from '../../helpers/getSelectedProfilesWithBoards';
import { canAddComment } from '../../helpers/canAddComment';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { getTotalHashes } from '../helpers/getTotalHashes';
import { TextVariationDragLayer } from '../TextVariationDragLayer';
import { serializeTextForDragAndDrop } from '../helpers/serializer';

export type VariationsDropZoneViewProps = {
  borderErrorType: SlateWrapperViewProps['borderErrorType'];
};

export const VariationsDropZoneView = ({ borderErrorType }: VariationsDropZoneViewProps): JSX.Element => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const { state, dispatch, profiles: socialProfiles } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const postGroup = state.postGroups[postGroupIndex];
  const { groupPostAttributes, posts, customizeContentPerProfile, profiles } = postGroup;
  const isCustomizingByProfile = customizeContentPerProfile && typeof customizeIndex === 'number';

  const texts = isCustomizingByProfile ? posts[customizeIndex].content.texts : groupPostAttributes.content.texts;
  const shouldShowFirstComment = isCustomizingByProfile
    ? posts[customizeIndex].content.shouldShowFirstComment
    : postGroup.groupPostAttributes.content.shouldShowFirstComment;
  const firstCommentText = isCustomizingByProfile
    ? shouldShowFirstComment
      ? posts[customizeIndex].content.firstComment
      : [initialParagraph()]
    : shouldShowFirstComment
    ? postGroup.groupPostAttributes.content.firstComment
    : [initialParagraph()];
  const postFeedType = isCustomizingByProfile
    ? posts[customizeIndex].content.postFeedType
    : postGroup.groupPostAttributes.content.postFeedType;
  const selectedProfiles = getSelectedProfilesWithBoards(
    profiles,
    socialProfiles,
    customizeIndex,
    customizeContentPerProfile,
    posts
  );
  const couldAddComment = canAddComment(selectedProfiles, postFeedType);
  const publishNextIndex = isCustomizingByProfile
    ? posts[customizeIndex].content.variationMeta.textVariationIndex
    : postGroup.groupPostAttributes.content.variationMeta.textVariationIndex;

  const shouldShowHashtagCounter =
    isCustomizingByProfile && posts[customizeIndex] && posts[customizeIndex].customizedProfileId
      ? !!selectedProfiles.find(
          item => item.id === posts[customizeIndex].customizedProfileId && item.__typename === 'InstagramSocialProfile'
        )
      : selectedProfiles.reduce((prev, current) => {
          if (prev) return prev;
          return current.__typename === 'InstagramSocialProfile';
        }, false);

  const addVariation = (currentId: number) => {
    dispatch({
      type: 'textAdd',
      payload: {
        postGroupIndex,
        currentId,
        customizeIndex
      }
    });
  };

  const addComment = () => {
    dispatch({
      type: 'toggleFirstComment',
      payload: {
        postGroupIndex,
        customizeIndex,
        selectedProfiles
      }
    });
  };

  const textsLength = texts.length;

  const [dndVariations, setDndVariations] = useState(texts);
  //a memoized function that uses js `immutability-helper` & `splice` to updateImmutable the
  //order of our rows
  const moveRow: MoveVariationRow = useCallback((dragIndex, hoverIndex) => {
    setDndVariations(prevVariations =>
      updateImmutable(prevVariations, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevVariations[dragIndex]]
        ]
      })
    );
  }, []);

  const applyDrop = useCallback(() => {
    dispatch({
      type: 'textDropDone',
      payload: {
        customizeIndex,
        postGroupIndex,
        texts: dndVariations
        // targetIndex: hoverIndex,
        // dragIndex
      }
    });
  }, [customizeIndex, dispatch, dndVariations, postGroupIndex]);

  const cancelDrop = useCallback(() => {
    setDndVariations(texts);
  }, [texts]);

  useEffect(() => {
    setDndVariations(prevVariations => {
      const isEqual =
        prevVariations.length === texts.length &&
        prevVariations.every(
          (val, index) =>
            val.id === texts[index].id &&
            serializeTextForDragAndDrop(texts[index].text) === serializeTextForDragAndDrop(val.text)
        );
      return isEqual ? prevVariations : texts;
    });
  }, [texts]);

  const renderDndVariations = () =>
    dndVariations.map(({ text, id }, index) => {
      const isLast = dndVariations.length === index + 1;
      const [hashtagsInText, hashtagsInFirstComment] = getTotalHashes([{ id, text }], firstCommentText);
      const hashtagsCount = hashtagsInText + hashtagsInFirstComment;
      const publishNext = dndVariations.length > 1 && publishNextIndex === index + 1;
      const canRemove = dndVariations.length > 1;
      return (
        <DraggableVariation
          isDragging={isDragging}
          key={`text-${id}`}
          borderErrorType={borderErrorType}
          publishNext={publishNext}
          showFirstComment={couldAddComment && shouldShowFirstComment}
          shouldAddComment={couldAddComment}
          addVariation={addVariation}
          addComment={addComment}
          shouldShowHashtagCounter={shouldShowHashtagCounter}
          hashtagsCount={hashtagsCount}
          isLast={isLast}
          customizeContentPerProfile={customizeContentPerProfile}
          canDrag={textsLength > 1}
          index={index}
          text={text}
          textId={id}
          moveRow={moveRow}
          cancelDrop={cancelDrop}
          applyDrop={applyDrop}
          canRemove={canRemove}
        />
      );
    });

  return (
    <>
      <TextVariationDragLayer
        setIsDragging={setIsDragging}
        showAddCommentButton={couldAddComment && !shouldShowFirstComment}
        firstCommentText={firstCommentText}
        shouldShowHashtagCounter={shouldShowHashtagCounter}
      />
      {renderDndVariations()}
    </>
  );
};
